import { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { Plus, Upload, Loader2, Pencil, Trash2, Ban } from 'lucide-react';

const EMPTY = { first_name: '', last_name: '', email: '', company: '', phone: '', tags: [], notes: '' };

export default function SubscribersTab({ meta }) {
  const [data, setData] = useState({ items: [], total: 0, counts: {} });
  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [suppression, setSuppression] = useState([]);

  const load = () => {
    api.get('/comms/subscribers', { params: { search: search || undefined, limit: 200 } })
      .then((r) => setData(r.data)).catch(() => {});
    if (meta.is_admin) api.get('/comms/suppression').then((r) => setSuppression(r.data)).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [search]);

  const del = async (s) => {
    if (!window.confirm(`Delete subscriber ${s.email}?`)) return;
    await api.delete(`/comms/subscribers/${s.id}`);
    toast.success('Subscriber deleted'); load();
  };

  return (
    <div className="space-y-4" data-testid="subscribers-tab">
      <p className="text-xs text-slate-500">External <strong>Newsletter Subscribers</strong> only — contacts who are NOT in the CRM. CRM partner contacts are targeted directly via the Recipient Builder in campaigns; the CRM remains the single source of truth.</p>
      <div className="flex flex-wrap items-center gap-2">
        <Input placeholder="Search newsletter subscribers…" value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 w-64" data-testid="subscriber-search" />
        <span className="text-xs text-slate-500">{data.counts.active ?? 0} active · {data.counts.unsubscribed ?? 0} unsubscribed</span>
        {meta.is_admin && (
          <div className="ml-auto flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setImportOpen(true)} data-testid="import-csv-btn">
              <Upload className="w-4 h-4 mr-1.5" /> Import CSV
            </Button>
            <Button size="sm" className="bg-magenta hover:bg-magenta/90" onClick={() => setDialog({ sub: null })} data-testid="add-subscriber-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Add Subscriber
            </Button>
          </div>
        )}
      </div>

      <div className="border border-slate-200 rounded-xl divide-y bg-white overflow-hidden">
        {data.items.map((s) => (
          <div key={s.id} className="px-4 py-2.5 flex flex-wrap items-center gap-2" data-testid={`subscriber-row-${s.id}`}>
            <div className="min-w-0 flex-1">
              <span className="font-medium text-navy text-sm">{s.first_name} {s.last_name}</span>
              <span className="text-xs text-slate-500 ml-2">{s.email}{s.company ? ` · ${s.company}` : ''}</span>
              <Badge className="bg-emerald-50 text-emerald-700 border-0 text-[10px] ml-2">NEWSLETTER</Badge>
              {s.status === 'unsubscribed' && <Badge className="bg-red-100 text-red-600 border-0 text-[10px] ml-1">UNSUBSCRIBED</Badge>}
            </div>
            <div className="flex flex-wrap gap-1">
              {(s.tags || []).map((t) => (
                <span key={t} className="text-[10px] bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">{t}</span>
              ))}
            </div>
            {meta.is_admin && (
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => setDialog({ sub: s })} data-testid={`edit-subscriber-${s.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="ghost" className="text-red-500" onClick={() => del(s)} data-testid={`delete-subscriber-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            )}
          </div>
        ))}
        {data.items.length === 0 && <p className="text-sm text-slate-400 text-center py-8">No newsletter subscribers yet. Add or import external contacts.</p>}
      </div>

      {meta.is_admin && suppression.length > 0 && (
        <div className="border border-red-100 bg-red-50/40 rounded-xl p-4" data-testid="suppression-list">
          <div className="flex items-center gap-2 text-sm font-semibold text-red-700 mb-2"><Ban className="w-4 h-4" /> Suppression List ({suppression.length})</div>
          <div className="flex flex-wrap gap-1.5">
            {suppression.map((x) => (
              <span key={x.email} className="text-xs bg-white border border-red-200 rounded-full px-2.5 py-1 flex items-center gap-1.5">
                {x.email} <span className="text-[10px] text-red-400 uppercase">{x.reason}</span>
                <button onClick={async () => { await api.delete(`/comms/suppression/${encodeURIComponent(x.email)}`); toast.success('Removed from suppression'); load(); }}
                  className="text-slate-400 hover:text-red-500" data-testid={`unsuppress-${x.email}`}>×</button>
              </span>
            ))}
          </div>
          <p className="text-[11px] text-red-400 mt-2">These addresses never receive campaigns (unsubscribed, bounced, or complained).</p>
        </div>
      )}

      {dialog && <SubscriberDialog sub={dialog.sub} onClose={() => { setDialog(null); load(); }} />}
      {importOpen && <ImportDialog onClose={() => { setImportOpen(false); load(); }} />}
    </div>
  );
}

function SubscriberDialog({ sub, onClose }) {
  const [form, setForm] = useState(sub ? { ...EMPTY, ...sub } : EMPTY);
  const [tagsText, setTagsText] = useState((sub?.tags || []).join(', '));
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!form.first_name.trim() || !form.email.trim()) { toast.error('First name and email are required'); return; }
    setBusy(true);
    try {
      const payload = {
        first_name: form.first_name, last_name: form.last_name, email: form.email, company: form.company,
        phone: form.phone, notes: form.notes, tags: tagsText.split(',').map((t) => t.trim()).filter(Boolean),
      };
      if (sub) await api.put(`/comms/subscribers/${sub.id}`, payload);
      else await api.post('/comms/subscribers', payload);
      toast.success(sub ? 'Subscriber updated' : 'Subscriber added');
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="subscriber-dialog">
        <DialogHeader><DialogTitle>{sub ? 'Edit Newsletter Subscriber' : 'Add Newsletter Subscriber'}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">First Name</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="sub-first-name" /></div>
          <div><Label className="text-xs">Last Name</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="sub-last-name" /></div>
          <div><Label className="text-xs">Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="sub-email" /></div>
          <div><Label className="text-xs">Company</Label><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="sub-company" /></div>
          <div><Label className="text-xs">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="sub-phone" /></div>
          <div><Label className="text-xs">Tags (comma-separated)</Label><Input value={tagsText} onChange={(e) => setTagsText(e.target.value)} placeholder="gala, community" data-testid="sub-tags" /></div>
        </div>
        <Button className="bg-magenta hover:bg-magenta/90 w-full" onClick={save} disabled={busy} data-testid="save-subscriber-btn">
          {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} {sub ? 'Update' : 'Add'} Subscriber
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function ImportDialog({ onClose }) {
  const [file, setFile] = useState(null);
  const [tagsText, setTagsText] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!file) { toast.error('Choose a CSV file'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('tags', JSON.stringify(tagsText.split(',').map((t) => t.trim()).filter(Boolean)));
      const r = await api.post('/comms/subscribers/import', fd);
      setResult(r.data);
      toast.success(`Imported: ${r.data.created} new, ${r.data.updated} updated`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Import failed'); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="import-dialog">
        <DialogHeader><DialogTitle>Import Newsletter Subscribers (CSV)</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Columns supported: email (required), first_name / name, last_name, company, phone. Existing emails are updated, never duplicated. For CRM contacts, use the CRM import instead.</p>
        <Input type="file" accept=".csv" onChange={(e) => setFile(e.target.files[0])} data-testid="import-file-input" />
        <div>
          <Label className="text-xs">Tag imported contacts (optional, comma-separated)</Label>
          <Input value={tagsText} onChange={(e) => setTagsText(e.target.value)} placeholder="community, event-2026" data-testid="import-tags-input" />
        </div>
        {result && (
          <div className="text-xs bg-slate-50 border rounded-lg p-3" data-testid="import-result">
            Created: <strong>{result.created}</strong> · Updated: <strong>{result.updated}</strong> · Skipped: <strong>{result.skipped}</strong>
            {result.errors?.length > 0 && <div className="text-red-500 mt-1">{result.errors.join('; ')}</div>}
          </div>
        )}
        <Button className="bg-magenta hover:bg-magenta/90 w-full" onClick={run} disabled={busy} data-testid="run-import-btn">
          {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Import
        </Button>
      </DialogContent>
    </Dialog>
  );
}
